import HomeUrls from 'lib/enums/HomeUrls';

const ONE_PAGE_SIZE = 1;
const TEN_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE = 20;
const HUNDRED_PAGE_SIZE = 100;
const DEFAULT_UPB_PAGE_SIZE = 50;
const DEFAULT_CATEGORY_VIEW_PAGE_SIZE = 100;
const NAVIGATION_SIDE_BAR_WIDTH = 70;
const UNCATEGORIZED = 'uncategorized';
const DEFAULT_THRESHOLD_VALUE_FOR_AI_TASKS = 0.4;
const CATEGORY_COLUMNS = [
  'l0_category',
  'l1_category',
  'l2_category',
  'l3_category',
  'l4_category',
];

const DEFAULT_PRODUCT_COLUMNS = [
  'name',
  'cnd_id',
  'brand_name',
  'sub_brand_name',
  'vendor',
  'vendor_sku',
  'pred_category',
  ...CATEGORY_COLUMNS,
];

const DEFAULT_VIEW = {
  name: 'Default View',
  columns: DEFAULT_PRODUCT_COLUMNS.map(column => ({ name: column, visible: true })),
};

const DEFAULT_GLOBAL_PRODUCT_COLUMNS = [
  'global_product_id',
  'name',
  'upc',
  'gtin',
  'manufacturer',
  ...CATEGORY_COLUMNS,
];
const GLOBAL_PRODUCT_COLUMNS = [
  ...DEFAULT_GLOBAL_PRODUCT_COLUMNS,
  'vendor',
  'vendor_sku',
  'brand',
  'categorized_by',
  'categorized_at',
  'size',
  'pack',
  'storage',
  'manufacturer_sku',
  'pack_size',
  'storage_temperature',
  'vendor_category',
  'vendor_subcategory',
  'qa_status',
  'annualized_gmv',
];

const CUT_DRY_NODE_URL = 'https://internal.cutanddry.com/admin/node/';

const CONFLICT_TYPES = {
  upcMismatch: 'UPC mismatch',
  gtinMismatch: 'GTIN mismatch',
  upcDuplicate: 'UPC duplicate',
  gtinDuplicate: 'GTIN duplicate',
};

const SORTABLE_GLOBAL_PRODUCT_COLUMNS = [
  'global_product_id',
  'name',
  'brand',
  'vendor',
  'manufacturer',
  'annualized_gmv',
];
const SORT_DIRECTIONS = { ASC: 'asc', DESC: 'desc' };

const GOOGLE_REDIRECT_URL = `${window.location.origin}/login`;

const ADMIN_ROLE = 'admin';

const GLOBAL_PRODUCTS_TABLE_ID = 'table-1';
const TAXONOMY_TABLE_ID = 'table-2';
const GLOBAL_PRODUCTS_TAXONOMY_TABLE_ID = 'table-3';
const ALL_PRODUCTS_TABLE_ID = 'table-4';
const GLOBAL_PRODUCTS_PANE = 'pane-1';
const DASHBOARD_WIDGETS = 'dashboardWidgets5';

const CATEGORY_LEVELS = {
  L0_CATEGORY: 'l0_category',
  L1_CATEGORY: 'l1_category',
  L2_CATEGORY: 'l2_category',
  L3_CATEGORY: 'l3_category',
  L4_CATEGORY: 'l4_category',
};

const SIDE_BAR_DISABLED_PATHS = [
  '/global-products/classify',
  '/all-products',
  '/manage-attributes',
  '/upb-viewer',
  '/upb-sales-viewer',
  '/upb-viewer-beta',
];

const DATA_SOURCES_VIEW_TABS = {
  ATTRIBUTES: 'Attributes',
  SKU_MAPPINGS: 'Sku Mappings',
  PRODUCTS: 'Products',
  SUBSCRIBED_MANUFACTURERS: 'Subscribed Manufacturers',
  SUMMARY: 'Summary',
  SETTINGS: 'Settings',
  CONCURRENT_SYNCS: 'Concurrent Syncs',
  PROCESS_LOGS: 'Process Logs',
};

const PRODUCT_DETAIL_VIEW_TABS = {
  PDP_PREVIEW: 'PDP Preview',
  ATTRIBUTES_V1: 'Attributes',
  ASSETS: 'Assets',
  RELATIONSHIPS: 'Relationships',
  CHANGE_LOG: 'Change Log',
};

const DATE_FORMAT_WITH_TIME = 'MM/DD/YYYY HH:mm:ss';
const DATE_FORMAT = 'MM/DD/YYYY';

const ATTRIBUTE_SECTIONS = {
  IMAGES: 'Images',
  CORE: 'Core',
  NUTRITION: 'Nutrition',
  HEALTH_AND_DIET: 'Health & Diet',
};

const CND_SYNC_STATUS = {
  YES: 'Yes',
  NO: 'No',
};

const SYSTEM_STATUS = {
  ARCHIVED: 'Archived',
  UNARCHIVED: 'Unarchived',
};

const THUMBNAIL_SOURCE_OPTIONS = [
  { value: null, label: 'All' },
  { value: -1, label: 'No Thumbnail' },
  { value: 'propagated', label: 'Propagated' },
  { value: 'distributor', label: 'Distributor/Manufacturer' },
  { value: 'fsa_upload', label: 'FSA Upload' },
  { value: 'data_source', label: 'Data source' },
];
const ATTRIBUTE_SAMPLE_FILE =
  // eslint-disable-next-line max-len
  'https://fsa-categorization-tool.s3.us-east-2.amazonaws.com/sample_files/attribute_assign_for_taxonomies_sample_file.xlsx';

const SCHEMA_ATTRIBUTE_SAMPLE_FILE =
  // eslint-disable-next-line max-len
  'https://fsa-categorization-tool.s3.us-east-2.amazonaws.com/sample_files/attribute_assign_for_schema_sample_file.csv';

const ATTRIBUTE_UPDATE_SAMPLE_FILE =
  // eslint-disable-next-line max-len
  'https://fsa-categorization-tool.s3.us-east-2.amazonaws.com/sample_files/sample_attribute_update_file.csv';

const SKU_UPDATE_SAMPLE_FILE =
  // eslint-disable-next-line max-len
  'https://fsa-categorization-tool.s3.us-east-2.amazonaws.com/sample_files/sample_sku_update_file.csv';

const IMAGE_TAGGER_HELP_GUIDE_URL =
  'https://cnd-spec-sheets.s3.us-east-2.amazonaws.com/fsa/image_types.pdf';

const ALL_SELECT_OPTION = { label: 'ALL', value: '' };

const ADVANCED_MAPPING_FUNCTIONS = {
  CONCAT: {
    name: 'CONCAT',
    description: "CONCAT(PACK_SIZE,' ',PACK_UOM)",
    insertValue: "CONCAT(SOURCE_ATTRIBUTE,' ',SOURCE_ATTRIBUTE)",
    isQuickAdd: false,
  },
  REPLACE: {
    name: 'REPLACE',
    description: 'REPLACE(SOURCE_ATTRIBUTE,[find1,find2],[replace1,replace2])',
    insertValue: 'REPLACE(SOURCE_ATTRIBUTE,[],[])',
    isQuickAdd: true,
  },
  SUBSTRING: {
    name: 'SUBSTRING',
    description: 'SUBSTRING(shelf_life,0,3) converts "365 days" to "365"',
    insertValue: 'SUBSTRING(SOURCE_ATTRIBUTE,start,length)',
    isQuickAdd: false,
  },
  NUMERIC: {
    name: 'NUMERIC',
    description: 'NUMERIC(net_weight_value) converts "10 kg" to "10"',
    insertValue: 'NUMERIC(SOURCE_ATTRIBUTE)',
    isQuickAdd: true,
  },
  NON_NUMERIC: {
    name: 'NON_NUMERIC',
    description: 'NON_NUMERIC(net_weight_with_uom) converts "10 kg" to "kg"',
    insertValue: 'NON_NUMERIC(SOURCE_ATTRIBUTE)',
    isQuickAdd: true,
  },
  CASE: {
    name: 'CASE',
    description: 'CASE(net_weight_with_uom,UPPER) available options: UPPER, LOWER',
    insertValue: 'CASE(SOURCE_ATTRIBUTE,UPPER)',
    isQuickAdd: false,
  },
  SEARCH_RETURN: {
    name: 'SEARCH_RETURN',
    description:
      'SEARCH_RETURN(glutamate,[NoMSG,NoneMSG],MSG FREE) search for NoMSG and NoneMDG in glutamate If found return MSG FREE',
    insertValue: 'SEARCH_RETURN(SOURCE_ATTRIBUTE,[search1,search2],return)',
    isQuickAdd: true,
  },
  SPLIT: {
    name: 'SPLIT',
    description:
      'SPLIT(ingredients,|,[1:],-]) splits ingredients by | and returns rest of the string starting from 1st index concatenated with -',
    insertValue: 'SPLIT(SOURCE_ATTRIBUTE,delimiter,[start:end],concatenation)',
    isQuickAdd: true,
  },
  ADD_COLUMN: {
    name: 'ADD_COLUMN',
    description:
      'ADD_COLUMN(case_weight, LBR) if a values exists on the source column, LBR will be added to the mapped FSA attribute',
    insertValue: 'ADD_COLUMN(SOURCE_ATTRIBUTE, column value)',
    isQuickAdd: false,
  },
  MULTI_MAP: {
    name: 'MULTI_MAP',
    description:
      'MULTI_MAP(description) value of the description column will be copied to the given source column',
    insertValue: 'MULTI_MAP(SOURCE_ATTRIBUTE)',
    isQuickAdd: false,
  },
  ADD_AFFIX: {
    name: 'ADD_AFFIX',
    description:
      'ADD_AFFIX(SOURCE_ATTRIBUTE, suffix, http://domain) "http://domain" will be added as a suffix to the selected source attribute if its available and mapped to the FSA attribute',
    insertValue: 'ADD_AFFIX(SOURCE_ATTRIBUTE,affix,string)',
    isQuickAdd: false,
  },
  ADD_DOCUMENT: {
    name: 'ADD_DOCUMENT',
    description:
      'ADD_DOCUMENT(document_url, document_name) the provided column value will be added to additional document under the document nutrition_doc',
    insertValue: 'ADD_DOCUMENT(SOURCE_ATTRIBUTE, document_name)',
    isQuickAdd: false,
  },
  IF_ELSE: {
    name: 'IF_ELSE',
    description:
      'IF_ELSE(CATCH_WEIGHT_FLAG|||Y|||CONCAT(PACKS_PER_CASE,"/",ITEM_SIZE)|||' +
      'CONCAT(PACKS_PER_CASE,"/",ITEM_SIZE," ",AVG_WEIGHT," Lb Avg.")) - ' +
      'Explanation: If CATCH_WEIGHT_FLAG equals Y, returns PACKS_PER_CASE/ITEM_SIZE. ' +
      'Otherwise, returns PACKS_PER_CASE/ITEM_SIZE AVG_WEIGHT Lb Avg.',
    insertValue: 'IF_ELSE(SOURCE_ATTRIBUTE|||condition_value|||true_expression|||false_expression)',
    isQuickAdd: false,
  },
  OR: {
    name: 'OR',
    description: 'OR(attr1,attr2,attr3) returns first valid value based on type',
    insertValue: 'OR(SOURCE_ATTRIBUTE,SOURCE_ATTRIBUTE,SOURCE_ATTRIBUTE)',
    isQuickAdd: true,
  },
  REMOVE_VALUE: {
    name: 'REMOVE_VALUE',
    description:
      'Applied automatically via the "Remove from list" checkbox below — wraps a mapping so its matched value is removed from a Multi-Value attribute instead of added.',
    insertValue: 'REMOVE_VALUE(SOURCE_ATTRIBUTE)',
    isQuickAdd: false,
  },
  SKIP_IF_CONTAINS: {
    name: 'SKIP_IF_CONTAINS',
    description:
      'SKIP_IF_CONTAINS(UPC_NUMBER_UNIT,X) skips the value (maps nothing) if it contains X, ' +
      'e.g. "126626262X" is skipped while "126626262" is brought in. ' +
      'Use bracket notation SKIP_IF_CONTAINS(UPC_NUMBER_UNIT,[X,x]) to match multiple strings',
    insertValue: 'SKIP_IF_CONTAINS(SOURCE_ATTRIBUTE,search_string)',
    isQuickAdd: true,
  },
  TRIM: {
    name: 'TRIM',
    description:
      'TRIM(PACK_SIZE,0) strips the given character(s) from the value, ' +
      'e.g. "0008 22.6 OZ" becomes "8 22.6 OZ". Characters are treated as a set. ' +
      'Optional third argument sets the side: leading (default), trailing, or both, ' +
      'e.g. TRIM(CODE,X,trailing) turns "12345XX" into "12345"',
    insertValue: 'TRIM(SOURCE_ATTRIBUTE,chars,leading)',
    isQuickAdd: true,
  },
};

const CUT_DRY_STATIC_PRODUCT_URL = 'CUT_DRY_STATIC_PRODUCT_URL';
const IMAGE_URL = 'image_urls';
const BRAND_LOGO = 'brand_logo';
const BRAND_DESCRIPTION = 'brand_description';
const QUANTITY_OF_IMAGES = 'quantity_of_images';
const NATIONAL_SKU = 'national_sku';
const NUMBER_OF_ACTIVE_LOCATIONS = 'number_of_active_locations';

const FILE_DROP_PROD_S3_LOCATION = 's3://fsa-categorization-tool/data_imports/file_drops/prod/';
const FILE_DROP_DEV_S3_LOCATION = 's3://fsa-categorization-tool/data_imports/file_drops/dev/';
const DATA_SOURCE_FILE_DROP_BUCKET = 's3://fsa-data-source-files/';

const CUT_DRY_CLIENT_ID = 1;

const GDSN_DATA_SOURCE_ID = 1043;
const PACKER_BRAND_ID = 631;

const BRAND_ATTRIBUTE_MAP = {
  name: 'name',
  manufacturerID: 'manufacturer_id',
  manufacturerName: 'manufacturer_name',
  logoURL: 'logo_url',
  description: 'description',
  brandURL: 'brand_url',
  keywords: 'keywords',
};

const USER_ROLES_NOT_TO_TRACK_ACTIVITY = [
  HomeUrls.ADMIN,
  HomeUrls.UPB_VIEWER,
  HomeUrls.USER,
  HomeUrls.UPB_VIEWER_ADMIN,
  HomeUrls.UPB_SALES_VIEWER,
  HomeUrls.SUPER_ADMIN,
];

const L2_CATEGORY_LEVEL = 'l2';
const L3_CATEGORY_LEVEL = 'l3';
const L4_CATEGORY_LEVEL = 'l4';

export {
  ADMIN_ROLE,
  ADVANCED_MAPPING_FUNCTIONS,
  BRAND_DESCRIPTION,
  BRAND_LOGO,
  ALL_PRODUCTS_TABLE_ID,
  ALL_SELECT_OPTION,
  ATTRIBUTE_SECTIONS,
  ATTRIBUTE_UPDATE_SAMPLE_FILE,
  ATTRIBUTE_SAMPLE_FILE,
  CATEGORY_COLUMNS,
  CATEGORY_LEVELS,
  CND_SYNC_STATUS,
  CONFLICT_TYPES,
  CUT_DRY_NODE_URL,
  CUT_DRY_STATIC_PRODUCT_URL,
  CUT_DRY_CLIENT_ID,
  IMAGE_URL,
  DASHBOARD_WIDGETS,
  DATA_SOURCE_FILE_DROP_BUCKET,
  DATA_SOURCES_VIEW_TABS,
  DATE_FORMAT_WITH_TIME,
  DATE_FORMAT,
  DEFAULT_CATEGORY_VIEW_PAGE_SIZE,
  DEFAULT_GLOBAL_PRODUCT_COLUMNS,
  ONE_PAGE_SIZE,
  TEN_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  HUNDRED_PAGE_SIZE,
  DEFAULT_PRODUCT_COLUMNS,
  DEFAULT_THRESHOLD_VALUE_FOR_AI_TASKS,
  DEFAULT_VIEW,
  DEFAULT_UPB_PAGE_SIZE,
  GDSN_DATA_SOURCE_ID,
  GLOBAL_PRODUCT_COLUMNS,
  GLOBAL_PRODUCTS_PANE,
  GLOBAL_PRODUCTS_TABLE_ID,
  GLOBAL_PRODUCTS_TAXONOMY_TABLE_ID,
  GOOGLE_REDIRECT_URL,
  IMAGE_TAGGER_HELP_GUIDE_URL,
  NATIONAL_SKU,
  NAVIGATION_SIDE_BAR_WIDTH,
  NUMBER_OF_ACTIVE_LOCATIONS,
  PACKER_BRAND_ID,
  PRODUCT_DETAIL_VIEW_TABS,
  QUANTITY_OF_IMAGES,
  SCHEMA_ATTRIBUTE_SAMPLE_FILE,
  SIDE_BAR_DISABLED_PATHS,
  SORT_DIRECTIONS,
  SORTABLE_GLOBAL_PRODUCT_COLUMNS,
  SYSTEM_STATUS,
  SKU_UPDATE_SAMPLE_FILE,
  TAXONOMY_TABLE_ID,
  THUMBNAIL_SOURCE_OPTIONS,
  UNCATEGORIZED,
  FILE_DROP_PROD_S3_LOCATION,
  FILE_DROP_DEV_S3_LOCATION,
  BRAND_ATTRIBUTE_MAP,
  USER_ROLES_NOT_TO_TRACK_ACTIVITY,
  L2_CATEGORY_LEVEL,
  L3_CATEGORY_LEVEL,
  L4_CATEGORY_LEVEL,
};
